import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

/**
 * Resolves a canonical English specialty name — as stored on member/template
 * payloads (`member.specialty`, `template.specialty`) — to its translated name
 * via the cached specialties list.
 *
 * Empty input renders the localized “Not specified” placeholder. Otherwise falls back
 * to the input verbatim: while the list is loading, and for names no longer in
 * the list (retired specialties, free text).
 */
export const useSpecialtyDisplayName = () => {
  const intl = useIntl();
  const { data: specialties } = useQuerySpecialties((d) => d.specialties);
  const byEnglishName = useMemo(
    () => new Map((specialties ?? []).map((s) => [s.name, s.localizedName])),
    [specialties]
  );
  return useCallback(
    (englishName: string) => {
      if (!englishName) {
        return intl.formatMessage({
          defaultMessage: 'Not specified',
          id: 'ms+ABHIdwQ',
          description: 'Placeholder shown when a member has no specialty set',
        });
      }
      return byEnglishName.get(englishName) ?? englishName;
    },
    [byEnglishName, intl]
  );
};
